import React, { useCallback, useEffect, useState } from 'react';
import ButtonMenuItemWithAnalytics from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/button-menu-item-with-analytics/index.tsx';
import { getProjectMenuItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/utils/getProjectMenuItemAnalytics.tsx';
import { useRovoEntitlementResource } from '@jira/router-resources__rovo-entitlement/src/useRovoEntitlementResource.tsx';
import { isWithinLast } from '@jira/platform__onboarding-core/src/utils/is-within-last/index.tsx';
import isMobileUserAgent from '@jira/platform__is-mobile-user-agent/src/index.tsx';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import { defineMessage, FormattedMessage } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useFirstActivationDateMs } from '@atlassian/jira-tenant-context-controller/src/components/first-activation-date-ms/index.tsx';
import { ImportChoiceModal } from './ImportChoiceModal.tsx';

const importWork = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.menu-item.label',
	defaultMessage: 'Import work',
	description: 'Label for the Import menu item in the project sidebar footer',
});

/**
 * ImportWorkMenuItem — shown in the sidenav footer for new admins on desktop in
 * their first 7 days.
 *
 * Clicking opens the ImportChoiceModal with two options:
 * 1. Import from a file (X2C import wizard)
 * 2. Import with Rovo (AI-assisted file upload)
 *
 * Ownership: `canCreateProject` (a generic, shared nav permission) and the
 * `jfind-retest-left-nav-import-killswitch` feature gate (an ops-level kill switch,
 * kept visible at the Footer boundary for incident-response visibility) are checked
 * by the parent Footer before this component is even mounted. Everything else that's
 * specific to this experiment — Rovo entitlement, site age, device, and the experiment
 * gate itself — lives here, so this team can iterate without needing Footer/Cosmos
 * review.
 *
 * Desktop only: the flow ends in a file upload handed to Rovo, which is awkward at
 * best on a phone, and the nav footer is a cramped target on small screens. Tablets
 * are excluded too, but a user who has flipped Jira's "desktop switch" counts as
 * desktop — they've opted into the full experience. This mirrors how the rest of Jira
 * reads the device, rather than introducing a second definition of "desktop".
 */
export const ImportWorkMenuItem = (): React.JSX.Element | null => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Hooks must be called unconditionally, before any early return below.
	const { data: rovoEntitlement } = useRovoEntitlementResource();
	const activationDateMs = useFirstActivationDateMs();

	const isRovoLLMEnabled = rovoEntitlement?.isRovoLLMEnabled ?? false;
	const isSiteWithin7Days =
		typeof activationDateMs === 'number' ? isWithinLast({ days: 7 })(activationDateMs) : false;

	// expVal is intentionally short-circuited behind the checks above (not a hook,
	// so this is safe) — it must only be evaluated for the truly eligible population,
	// otherwise we'd pollute the experiment's exposure logging with ineligible users.
	// The desktop check has to sit on this side of expVal for the same reason.
	const isInExperiment =
		isRovoLLMEnabled &&
		isSiteWithin7Days &&
		!isMobileUserAgent() &&
		expVal('jfind-retest-left-nav-import', 'isEnabled', false);

	const importWorkAnalytics = getProjectMenuItemAnalytics({
		itemId: 'importWork',
	});

	useEffect(() => {
		if (!isInExperiment) {
			return;
		}
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'importWorkMenuItem',
			}),
		);
	}, [isInExperiment, createAnalyticsEvent]);

	const handleClick = useCallback(() => {
		// ButtonMenuItemWithAnalytics already fires the generic `navigationItem clicked`
		// event that every sidenav item emits. This dedicated event is fired alongside it
		// so the experiment funnel can be measured without having to filter that shared
		// event down by `itemId`, and so it pairs 1:1 with `importWorkMenuItem viewed`.
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'importWorkMenuItem',
			}),
		);
		setIsModalOpen(true);
	}, [createAnalyticsEvent]);

	const handleModalClose = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	if (!isInExperiment) {
		return null;
	}

	return (
		<>
			<ButtonMenuItemWithAnalytics
				onClick={handleClick}
				elemBefore={<CloudArrowUpIcon label="" color="currentColor" />}
				analytics={importWorkAnalytics}
				data-testid="sidebar-nav4-sidebars-content-projects-import-work.menu-item"
			>
				<FormattedMessage {...importWork} />
			</ButtonMenuItemWithAnalytics>
			<ImportChoiceModal isOpen={isModalOpen} onClose={handleModalClose} />
		</>
	);
};
